import React from 'react';
import styles from './AboutPage.module.css';

const features = [
  {
    title: 'Chat workspace',
    description: 'Ask Policyora about a regulation, upload context, or continue an existing thread. Every chat keeps citations so you can trust the answer.',
    link: '/app',
    linkLabel: 'Open chat',
    accent: 'chatAccent',
  },
  {
    title: 'Search console',
    description: 'Use the dedicated search page to run a scoped query, review recent searches, and jump straight to detailed results.',
    link: '/search',
    linkLabel: 'Go to search',
    accent: 'searchAccent',
  },
  {
    title: 'Regulatory dashboard',
    description: 'Track the map, recent actions, and saved alerts from a single dashboard designed for policy and product reviews.',
    link: '/dashboard',
    linkLabel: 'View dashboard',
    accent: 'dashboardAccent',
  },
  {
    title: 'Comparison view',
    description: 'Compare regions or frameworks side-by-side so legal and GTM teams can align on requirements in minutes.',
    link: '/comparison',
    linkLabel: 'Compare jurisdictions',
    accent: 'comparisonAccent',
  },
  {
    title: 'Profile & access controls',
    description: 'Update onboarding details, manage passwords, and keep your workspace secure from the profile section.',
    link: '/profile',
    linkLabel: 'Manage profile',
    accent: 'profileAccent',
  },
];

const workflows = [
  {
    title: 'Ask & explore',
    description: 'Start in chat or search to gather the latest AI policy context with source links.',
  },
  {
    title: 'Compare & document',
    description: 'Use the dashboard and comparison view to capture differences across markets or time periods.',
  },
  {
    title: 'Share & brief',
    description: 'Move insights from chat or dashboard straight into stakeholder updates with citations attached.',
  },
];

export const AboutPage: React.FC = () => {
  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>Policyora · AI governance workspace</p>
          <h1 className={styles.heroTitle}>One place for chat, search, dashboards, and history</h1>
          <p className={styles.heroSubtitle}>
            Every feature you see inside Policyora is live today—from the chat workspace and regulatory dashboard to
            comparison tables and saved histories. This page outlines what already exists so your team knows exactly what
            to rely on.
          </p>
        </div>
        <div className={styles.heroActions}>
          <a href="/app" className={styles.primaryAction}>Open chat workspace</a>
          <a href="/dashboard" className={styles.secondaryAction}>Visit dashboard</a>
        </div>
      </section>

      <section className={styles.featuresGrid}>
        {features.map((feature) => (
          <article key={feature.title} className={styles.featureCard}>
            <div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
            <a
              href={feature.link}
              className={`${styles.featureLink} ${styles[feature.accent] ?? ''}`}
            >
              {feature.linkLabel}
            </a>
          </article>
        ))}
      </section>

      <section className={styles.workflow}>
        <div className={styles.workflowHeader}>
          <h2>How teams work inside Policyora</h2>
          <p>These are the exact steps users take today across chat, search, dashboard, and comparison.</p>
        </div>
        <div className={styles.workflowSteps}>
          {workflows.map((step, index) => (
            <div key={step.title} className={styles.workflowStep}>
              <div className={styles.stepBadge}>0{index + 1}</div>
              <div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.cta}>
        <div>
          <h3>Work from the Policyora features you already have</h3>
          <p>
            Hop into a chat, pull the dashboard during stand-up, or compare markets before launch. Everything described
            here is live and ready.
          </p>
        </div>
        <div className={styles.ctaActions}>
          <a href="/app" className={styles.primaryAction}>Start a new chat</a>
          <a href="/dashboard" className={styles.secondaryAction}>Open dashboard</a>
        </div>
      </section>
    </div>
  );
};
